import React, { useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSite } from '@/contexts/SiteContext';
import { useUrlInspect, useCatalogFunnel, UrlInspectResult } from '@/hooks/useCatalog';
import { LinkedViewsStrip } from '@/components/catalog/LinkedViewsStrip';
import { StagePill } from '@/components/catalog/StagePill';
import { SOURCE_LABELS } from '@/components/catalog/sourceLabels';
import { FactRow } from '@/components/catalog/FactRow';
import { catalogColors } from '@/components/catalog/catalogColors';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertCircle,
  Search, ExternalLink, ImageOff,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function relTime(iso: string | null | undefined) {
  if (!iso) return '—';
  try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); } catch { return '—'; }
}

function asString(v: unknown): string | undefined {
  if (v === null || v === undefined || v === '') return undefined;
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return undefined;
}

function asRecord(v: unknown): Record<string, unknown> | undefined {
  if (v !== null && typeof v === 'object' && !Array.isArray(v)) return v as Record<string, unknown>;
  return undefined;
}

// Flatten nested object into dot-notation key/value pairs (one level deep)
function flatten(obj: Record<string, unknown>, prefix = ''): Array<[string, string]> {
  const out: Array<[string, string]> = [];
  for (const [k, v] of Object.entries(obj)) {
    const key = prefix ? `${prefix}.${k}` : k;
    if (v === null || v === undefined || v === '') continue;
    const nested = asRecord(v);
    if (nested) {
      out.push(...flatten(nested, key));
    } else if (Array.isArray(v)) {
      out.push([key, `[${v.length} items]`]);
    } else {
      out.push([key, asString(v) ?? '']);
    }
  }
  return out;
}

// ─── Overall banner ───────────────────────────────────────────────────────────


// ─── Product card banner (top, merges product_identification) ─────────────────

function ProductCardBanner({
  product,
  identification,
  apgId,
}: {
  product: NonNullable<UrlInspectResult['product_card']>;
  identification?: Record<string, unknown>;
  apgId?: string;
}) {
  const navigate = useNavigate();
  const [errored, setErrored] = useState(false);

  const get = (k: string): string | undefined =>
    asString(identification?.[k]) ?? asString((product as unknown as Record<string, unknown>)[k]);

  const img = product.image_url;
  const showImg = !!img && !errored;

  return (
    <div className="rounded-lg border p-4 flex items-start gap-4 bg-card">
      {/* Thumbnail */}
      <div className="w-24 h-24 rounded shrink-0 border bg-muted overflow-hidden flex items-center justify-center">
        {showImg ? (
          <img
            src={img!}
            alt={product.product_name}
            className="w-full h-full object-cover"
            onError={() => setErrored(true)}
          />
        ) : (
          <ImageOff className="h-6 w-6 text-muted-foreground/50" />
        )}
      </div>

      {/* Identification facts */}
      <div className="flex-1 min-w-0 space-y-2">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-base font-semibold leading-tight">{product.product_name}</div>
            {product.brand && <div className="text-xs text-muted-foreground mt-0.5">{product.brand}</div>}
          </div>
          <StagePill state={product.source} label={SOURCE_LABELS[product.source] ?? product.source} />
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 pt-1">
          <FactRow label="product_id"     value={get('product_id')} mono />
          <FactRow label="pfi UUID"       value={get('pfi_id') ?? product.pfi_id} mono />
          <FactRow label="GTIN"           value={get('gtin')} mono />
          <FactRow label="MPN"            value={get('mpn')} mono />
          <FactRow label="Category"       value={get('category')} />
          <FactRow label="Custom label 4" value={get('custom_label_4')} />
          <FactRow label="Feed status"    value={get('feed_status')} />
        </div>

        <div className="flex items-center gap-3 pt-2 text-xs">
          <button
            className="hover:underline flex items-center gap-1"
            style={{ color: catalogColors.accentTeal }}
            onClick={() => navigate(`/catalog/monitor?q=${encodeURIComponent(product.product_id)}`)}
          >
            Open in Catalog Monitor →
          </button>
          {product.product_url && (
            <a
              href={product.product_url}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline flex items-center gap-1"
              style={{ color: catalogColors.accentTeal }}
            >
              <ExternalLink className="h-3 w-3" />
              View on site
            </a>
          )}
          {apgId && (
            <button
              className="hover:underline flex items-center gap-1"
              style={{ color: catalogColors.accentTeal }}
              onClick={() => navigate(`/content-review?id=${apgId}&type=product_aeo`)}
            >
              Open in Content Review →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Section primitives ──────────────────────────────────────────────────────

function Section({ title, status, children }: { title: string; status?: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2 pb-4 border-b last:border-b-0">
      <div className="flex items-center gap-2">
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        {status && status !== 'ok' && <StatusBadge status={status} />}
      </div>
      <div className="pl-1 space-y-1">{children}</div>
    </section>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { bg: string; fg: string; label: string }> = {
    ok:      { bg: catalogColors.accentTeal + '20', fg: catalogColors.accentTeal, label: 'OK' },
    warn:    { bg: catalogColors.amber + '20',      fg: catalogColors.amber,      label: 'WARN' },
    pending: { bg: catalogColors.amber + '20',      fg: catalogColors.amber,      label: 'PENDING' },
    error:   { bg: '#EF444420',                     fg: '#EF4444',                label: 'ERROR' },
    missing: { bg: catalogColors.gray + '30',       fg: catalogColors.gray,       label: 'MISSING' },
  };
  const c = map[status] ?? map.missing;
  return (
    <span
      className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide"
      style={{ backgroundColor: c.bg, color: c.fg }}
    >
      {c.label}
    </span>
  );
}

// ─── Translation language pill ────────────────────────────────────────────────

const TRANSLATION_STATUS_COLORS: Record<string, { bg: string; fg: string }> = {
  completed: { bg: catalogColors.accentTeal + '20', fg: catalogColors.accentTeal },
  ok:        { bg: catalogColors.accentTeal + '20', fg: catalogColors.accentTeal },
  stale:     { bg: catalogColors.amber + '20',      fg: catalogColors.amber },
  pending:   { bg: catalogColors.amber + '20',      fg: catalogColors.amber },
  failed:    { bg: '#EF444420',                     fg: '#EF4444' },
  missing:   { bg: catalogColors.gray + '30',       fg: catalogColors.gray },
};

function TranslationCell({
  language, status, translated_at,
}: {
  language: string;
  status: string;
  translated_at?: string | null;
}) {
  const c = TRANSLATION_STATUS_COLORS[status] ?? TRANSLATION_STATUS_COLORS.missing;
  return (
    <div
      className="flex flex-col items-center gap-0.5 px-2 py-1.5 rounded border text-[10px] min-w-[60px]"
      style={{ borderColor: c.fg + '40', backgroundColor: c.bg }}
    >
      <span className="font-semibold uppercase tracking-wide text-muted-foreground">{language}</span>
      <span className="font-medium uppercase" style={{ color: c.fg }}>{status}</span>
      {translated_at && (
        <span className="text-muted-foreground text-[9px]" title={translated_at}>
          {relTime(translated_at)}
        </span>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function UrlInspector() {
  const { selectedSite, setSelectedSite, sites } = useSite();
  const [searchParams, setSearchParams] = useSearchParams();
  const [inputVal, setInputVal] = useState(searchParams.get('q') || '');

  const siteId   = selectedSite?.id || selectedSite?._id;
  const queryUrl = searchParams.get('q') || undefined;

  const { data, isLoading, error } = useUrlInspect(queryUrl, siteId);
  // Read canonical_mode flag from the funnel response (same site context)
  const { data: funnelData } = useCatalogFunnel(siteId);
  const canonicalMode = funnelData?.funnel?.canonical_mode ?? false;

  const handleInspect = useCallback(() => {
    if (!inputVal.trim()) return;
    setSearchParams({ q: inputVal.trim() }, { replace: false });
  }, [inputVal, setSearchParams]);

  // Cross-site auto-switch handler is no longer needed since occurrences section is deleted.
  void setSelectedSite; void sites;

  // Extract per-signal data by label match
  const signalByLabel = (label: string) =>
    data?.signals.find(s => s.label?.toLowerCase().includes(label.toLowerCase()));

  // apg_id is used by the Product Card 'Open in Content Review' deep link.
  const aeoGenSignal = data?.signals.find(s => s.label?.toLowerCase().includes('aeo') || asRecord(s.detail)?.apg_id);
  const apgIdForReview = asString(asRecord(aeoGenSignal?.detail)?.apg_id);

  const identificationDetail = asRecord(signalByLabel('product identification')?.detail);
  const feedPresenceDetail   = asRecord(signalByLabel('feed presence')?.detail);
  const importStatusDetail   = asRecord(signalByLabel('import status')?.detail);
  const enrichmentDetail     = asRecord(signalByLabel('enrichment')?.detail);
  const enrichmentSignal     = signalByLabel('enrichment');
  const canonicalSignal      = signalByLabel('canonical');
  const canonicalDetail      = asRecord(canonicalSignal?.detail);
  const aeoSignal            = signalByLabel('aeo generation');
  const aeoDetail            = asRecord(aeoSignal?.detail);
  const translationsSignal   = signalByLabel('translations');
  const fieldsSignal         = signalByLabel('fields generated');
  const publishSignal        = signalByLabel('publish');
  const publishDetail        = asRecord(publishSignal?.detail);
  const analyticsSignal      = signalByLabel('analytics ingestion');
  const analyticsDetail      = asRecord(analyticsSignal?.detail);

  return (
    <div className="p-6 max-w-[1100px] mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold">URL Inspector</h1>
          <p className="text-sm text-muted-foreground mt-1">Inspect a product URL across the pipeline</p>
        </div>
        <LinkedViewsStrip />
      </div>

      {/* URL input bar */}
      <div className="flex gap-2 max-w-2xl">
        <Input
          value={inputVal}
          onChange={e => setInputVal(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleInspect(); }}
          placeholder="Paste a product URL…"
          className="h-9 text-sm font-mono"
        />
        <Button onClick={handleInspect} className="h-9 px-4" style={{ backgroundColor: catalogColors.accentTeal }}>
          <Search className="h-4 w-4 mr-1.5" />
          Inspect
        </Button>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-sm text-destructive p-3 rounded-lg bg-destructive/10">
          <AlertCircle className="h-4 w-4 shrink-0" />{(error as Error).message}
        </div>
      )}

      {isLoading && (
        <div className="space-y-4">
          <Skeleton className="h-32 rounded-lg" />
          {Array.from({ length: 7 }).map((_, i) => <Skeleton key={i} className="h-20 rounded" />)}
        </div>
      )}

      {data && (
        <div className="space-y-6">
          {/* Product Card banner (merged with #1 product_identification) */}
          {data.product_card && (
            <ProductCardBanner product={data.product_card} identification={identificationDetail} apgId={apgIdForReview} />
          )}

          {/* Sections (top-to-bottom) */}
          <div className="space-y-6">

            {/* #2+#3 Feed history (merge feed_presence + import_status) */}
            <Section
              title="Feed history"
              status={feedPresenceDetail || importStatusDetail
                ? (signalByLabel('import status')?.status ?? signalByLabel('feed presence')?.status)
                : 'missing'}
            >
              <FactRow label="Feed name"      value={asString(feedPresenceDetail?.source_name) ?? asString(feedPresenceDetail?.feed_name)} />
              <FactRow label="Source id"      value={asString(feedPresenceDetail?.source_id) ?? asString(feedPresenceDetail?.feed_import_config_id)} mono />
              <FactRow label="Feed URL"       value={asString(feedPresenceDetail?.feed_url)} mono />
              <FactRow label="First imported" value={asString(importStatusDetail?.first_imported_at) ?? asString(feedPresenceDetail?.first_imported_at)} />
              <FactRow label="Last seen"      value={asString(importStatusDetail?.last_seen_in_feed_at) ?? asString(feedPresenceDetail?.last_seen_in_feed_at)} />
              <FactRow label="Feed status"    value={asString(feedPresenceDetail?.feed_status) ?? asString(identificationDetail?.feed_status)} />
            </Section>

            {/* #4 Enrichment — all kbe_metadata flattened */}
            {enrichmentSignal && (
              <Section title="Enrichment" status={enrichmentSignal.status}>
                {(() => {
                  if (!enrichmentDetail) {
                    return <div className="text-xs text-muted-foreground">No enrichment data.</div>;
                  }
                  // Pull the per-attribute value: each entry can be a primitive or { value, source, confidence, ... }
                  const formatAttrValue = (v: unknown): string | null => {
                    if (v === null || v === undefined || v === '') return null;
                    if (Array.isArray(v)) return v.map(x => asString(x) ?? JSON.stringify(x)).join(', ');
                    const obj = asRecord(v);
                    if (obj) {
                      // Common shape: { value, source, confidence, _validation }
                      if ('value' in obj) return formatAttrValue(obj.value);
                      // Fall back to a flattened representation
                      return Object.entries(obj)
                        .filter(([, vv]) => vv !== null && vv !== undefined && vv !== '')
                        .map(([kk, vv]) => `${kk}: ${asString(vv) ?? ''}`)
                        .join(' · ');
                    }
                    return asString(v);
                  };

                  const attrs = asRecord(enrichmentDetail.extracted_attributes);
                  const headerRows = ([
                    ['kbe_id',          asString(enrichmentDetail.kbe_id) ?? ''],
                    ['approval_status', asString(enrichmentDetail.approval_status) ?? ''],
                    ['active',          asString(enrichmentDetail.active) ?? ''],
                    ['category_leaf',   asString(enrichmentDetail.category_leaf) ?? ''],
                    ['last_enriched',   asString(enrichmentDetail.last_enriched) ?? ''],
                  ] as Array<[string, string]>).filter(([, v]) => v !== '');

                  const attrRows: Array<[string, string]> = attrs
                    ? Object.entries(attrs)
                        .map<[string, string | null]>(([k, v]) => [k, formatAttrValue(v)])
                        .filter((entry): entry is [string, string] => entry[1] !== null && entry[1] !== '')
                    : [];

                  return (
                    <div className="space-y-3">
                      {headerRows.length > 0 && (
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 pb-2 border-b border-border/40">
                          {headerRows.map(([k, v]) => (
                            <FactRow key={k} label={k} value={v} mono={k === 'kbe_id'} />
                          ))}
                        </div>
                      )}
                      {attrRows.length > 0 ? (
                        <div>
                          <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">
                            Extracted attributes ({attrRows.length})
                          </div>
                          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                            {attrRows.map(([k, v]) => <FactRow key={k} label={k} value={v} />)}
                          </div>
                        </div>
                      ) : (
                        <div className="text-xs text-muted-foreground">No extracted attributes.</div>
                      )}
                    </div>
                  );
                })()}
              </Section>
            )}

            {/* #5 Canonical selection — only when canonical mode is enabled */}
            {canonicalMode && canonicalSignal && (
              <Section title="Canonical selection" status={canonicalSignal.status}>
                {canonicalDetail && Object.keys(canonicalDetail).length > 0 ? (
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                    {flatten(canonicalDetail).map(([k, v]) => (
                      <FactRow key={k} label={k} value={v} mono={k.includes('id')} />
                    ))}
                  </div>
                ) : (
                  <div className="text-xs text-muted-foreground">No canonical data.</div>
                )}
              </Section>
            )}

            {/* #6 AEO content (drop pagemind_page_url) */}
            {aeoSignal && (
              <Section title="AEO content" status={aeoSignal.status}>
                {aeoDetail && Object.keys(aeoDetail).length > 0 ? (
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                    {Object.entries(aeoDetail)
                      .filter(([k]) => k !== 'pagemind_page_url')
                      .map(([k, v]) => {
                        const valStr = asString(v);
                        if (!valStr) return null;
                        return <FactRow key={k} label={k} value={valStr} mono={k === 'apg_id' || k.includes('id')} />;
                      })}
                  </div>
                ) : (
                  <div className="text-xs text-muted-foreground">No AEO content yet.</div>
                )}
              </Section>
            )}

            {/* #7 Translations — per-language grid */}
            {translationsSignal && (
              <Section title="Translations" status={translationsSignal.status}>
                {(() => {
                  const langs = Array.isArray(translationsSignal.detail)
                    ? translationsSignal.detail as Array<Record<string, unknown>>
                    : [];
                  if (langs.length === 0) {
                    return <div className="text-xs text-muted-foreground">No translation records.</div>;
                  }
                  // Summary line
                  const counts: Record<string, number> = {};
                  langs.forEach(l => {
                    const s = String(l.status ?? 'missing');
                    counts[s] = (counts[s] ?? 0) + 1;
                  });
                  const summary = Object.entries(counts).map(([s, n]) => `${n} ${s}`).join(' · ');

                  return (
                    <>
                      <div className="text-xs text-muted-foreground mb-2">{langs.length} languages: {summary}</div>
                      <div className="flex flex-wrap gap-1.5">
                        {langs.map(l => (
                          <TranslationCell
                            key={String(l.language)}
                            language={String(l.language)}
                            status={String(l.status ?? 'missing')}
                            translated_at={asString(l.translated_at)}
                          />
                        ))}
                      </div>
                    </>
                  );
                })()}
              </Section>
            )}

            {/* #8 Fields generated — present/missing list only */}
            {fieldsSignal && (
              <Section title="Fields generated" status={fieldsSignal.status}>
                {(() => {
                  const items = Array.isArray(fieldsSignal.detail)
                    ? fieldsSignal.detail as Array<Record<string, unknown>>
                    : [];
                  if (items.length === 0) {
                    return <div className="text-xs text-muted-foreground">No field data.</div>;
                  }
                  const present = items.filter(i => i.present === true).map(i => String(i.field));
                  const missing = items.filter(i => i.present === false).map(i => String(i.field));
                  return (
                    <div className="space-y-1.5 text-xs">
                      <div>
                        <span className="font-medium text-foreground">Present ({present.length}):</span>{' '}
                        <span className="text-muted-foreground">{present.join(', ') || '—'}</span>
                      </div>
                      <div>
                        <span className="font-medium text-foreground">Missing ({missing.length}):</span>{' '}
                        <span style={{ color: missing.length > 0 ? catalogColors.amber : undefined }}>
                          {missing.join(', ') || '—'}
                        </span>
                      </div>
                    </div>
                  );
                })()}
              </Section>
            )}

            {/* #9 Publish status — filter out keys already shown in #6 AEO content */}
            {publishSignal && (() => {
              // aeo_status and pagemind_page_url overlap with the AEO content section above.
              // manifest_config_url is surfaced explicitly at the top of the section, so we
              // also exclude it from the flattened fact-row grid to avoid duplication.
              const EXCLUDE = new Set(['aeo_status', 'pagemind_page_url', 'manifest_config_url']);
              const manifestConfigUrl = asString(publishDetail?.manifest_config_url);
              const filteredRows = publishDetail
                ? flatten(publishDetail).filter(([k]) => !EXCLUDE.has(k.split('.')[0]))
                : [];
              return (
                <Section title="Publish status" status={publishSignal.status}>
                  {manifestConfigUrl && (
                    <div className="pb-2 mb-2 border-b border-border/40">
                      <FactRow label="Manifest feed URL" value={manifestConfigUrl} mono />
                    </div>
                  )}
                  {filteredRows.length > 0 ? (
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                      {filteredRows.map(([k, v]) => (
                        <FactRow key={k} label={k} value={v} mono={k.includes('id') || k.includes('url')} />
                      ))}
                    </div>
                  ) : (
                    !manifestConfigUrl && <div className="text-xs text-muted-foreground">No publish data.</div>
                  )}
                </Section>
              );
            })()}

            {/* #11 Performance data sources */}
            {analyticsSignal && (
              <Section title="Performance data sources" status={analyticsSignal.status}>
                {(() => {
                  const surfaces: Array<[string, string]> = [
                    ['GSC',              'gsc'],
                    ['GA4',              'ga4'],
                    ['Google Ads',       'google_ads'],
                    ['Merchant Center',  'merchant_center'],
                  ];
                  return (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {surfaces.map(([label, key]) => {
                        const surface = asRecord(analyticsDetail?.[key]);
                        const status = asString(surface?.status) ?? 'missing';
                        return (
                          <div
                            key={key}
                            className="flex items-center justify-between px-2 py-1.5 rounded border text-xs"
                          >
                            <span className="font-medium">{label}</span>
                            <StatusBadge status={
                              status === 'active' ? 'ok' :
                              status === 'pending_backfill' ? 'pending' :
                              status === 'not_configured' || status === 'configured_no_data' ? 'warn' :
                              'missing'
                            } />
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </Section>
            )}
          </div>
        </div>
      )}

      {!queryUrl && !isLoading && (
        <div className="text-center py-20 text-muted-foreground">
          Enter a product URL above to inspect the pipeline.
        </div>
      )}
    </div>
  );
}
